import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import de from './locales/de';
import en from './locales/en';
import tr from './locales/tr';
import fr from './locales/fr';
import it from './locales/it';
import nl from './locales/nl';
import { updatePreferences } from '../api/api';

export type Locale = 'de' | 'en' | 'tr' | 'fr' | 'it' | 'nl';

type TranslationKeys = keyof typeof de;
type Translations = Record<string, string>;

export const locales: Record<Locale, Translations> = { de, en, tr, fr, it, nl };

export const LOCALE_LABELS: Record<Locale, string> = {
  de: 'Deutsch',
  en: 'English',
  tr: 'Türkçe',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  de: '🇩🇪',
  en: '🇬🇧',
  tr: '🇹🇷',
  fr: '🇫🇷',
  it: '🇮🇹',
  nl: '🇳🇱',
};

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const STORAGE_KEY = 'alex-bet-locale';

function getInitialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (saved === 'de' || saved === 'en' || saved === 'tr' || saved === 'fr' || saved === 'it' || saved === 'nl')) {
      return saved as Locale;
    }
  } catch {}
  return 'de';
}

const I18nContext = createContext<I18nContextType>({
  locale: 'de',
  setLocale: () => {},
  t: (key: string) => key,
});

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
      // Fire and forget updating the backend preference
      if (localStorage.getItem('access_token')) {
        updatePreferences({ locale: newLocale }).catch(e => console.warn('Failed to update language preference', e));
      }
    } catch {}
  }, []);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    let text = locales[locale]?.[key] ?? locales['de']?.[key] ?? key;
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(`{${paramKey}}`, String(value));
      });
    }
    return text;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);

export default I18nContext;
